import React, { useRef, useEffect } from 'react';
import * as Quill from 'quill';
import 'react-quill/dist/quill.snow.css';
import { isNil } from 'lodash';
import styled from '@emotion/styled';
import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';

import { OnBlurCallback, OnChangeCallback } from '.';

import { Tooltip } from '..';
import { ComponentProps as $RichTextEditorProps } from '../../types/react-quill/types';
import theme from '../../theme';

export interface IVariants {
  [key: string]: React.ReactElement;
}

const RTEContainer = styled.div`
  .ql-tooltip {
    left: 23% !important;
    top: 10% !important;
  }
`;

const WarningText = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  color: ${theme.colorsErrorState100};
`;

const TOOLBAR_CLASS_NAME = 'ql-toolbar';
const SELECT_CLASS_NAME = 'ql-header';
const CustomToolbar = ({ id = 'toolbar', variant = 'default' }) => {
  const variants: IVariants = {
    default: (
      <>
        <select className={SELECT_CLASS_NAME} defaultValue={''}>
          <option value="1" label="Heading 1">
            Heading 1
          </option>
          <option value="2" label="Heading 2">
            Heading 2
          </option>
          <option value="3" label="Heading 3">
            Heading 3
          </option>
          <option value="" label="Paragraph">
            Paragraph
          </option>
        </select>
        <button title="bold text" aria-label="toggle bold text" className="ql-bold" />
        <button title="italic text" aria-label="toggle italic text" className="ql-italic" />
        <button title="underline text" aria-label="toggle underline text" className="ql-underline" />
        <button title="align left" aria-label="toggle left align" className="ql-align" value="" />
        <button title="align center" aria-label="toggle left align" className="ql-align" value="center" />
        <button title="align right" aria-label="toggle left align" className="ql-align" value="right" />
        <button title="align justify" aria-label="toggle left align" className="ql-align" value="justify" />
        <button title="bulleted list" aria-label="toggle bulleted list" className="ql-list" value="bullet" />
        <button title="indent left" aria-label="toggle bulleted list" className="ql-indent" value="-1" />
        <button title="indent right" aria-label="toggle bulleted list" className="ql-indent" value="+1" />
        <button title="apply link" aria-label="apply linked text" className="ql-link" value="link" />
      </>
    ),
    // Eventbrite API has limited support for html tags
    // (https://www.eventbrite.com/platform/docs/event-description#text-module)
    publicEvent: (
      <>
        <select className={SELECT_CLASS_NAME} defaultValue={''}>
          <option value="1" label="Heading 1">
            Heading 1
          </option>
          <option value="2" label="Heading 2">
            Heading 2
          </option>
          <option value="3" label="Heading 3">
            Heading 3
          </option>
          <option value="" label="Paragraph">
            Paragraph
          </option>
        </select>
        <button title="bold text" aria-label="toggle bold text" className="ql-bold" />
        <button title="bulleted list" aria-label="toggle bulleted list" className="ql-list" value="bullet" />
        <button title="apply link" aria-label="apply linked text" className="ql-link" value="link" />
      </>
    ),
  };
  return <div id={id}>{variants[variant]}</div>;
};

const modules = (toolbarId: string) => {
  return {
    toolbar: {
      container: `#${toolbarId}`,
      handlers: {},
    },
    keyboard: {
      bindings: {
        tab: false,
        'list autofill': {
          prefix: /^\s{0,}(1){1,1}(\.|-|\*|\[ ?\]|\[x\])$/,
        },
      },
    },
    clipboard: {
      matchVisual: false,
    },
  };
};

let toolbarCnt = 0;
/* https://quilljs.com/docs/formats/ Ensure all formats are included so that "copy paste" works for all supported HTML format */
const formats = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'list',
  'bullet',
  'indent',
  'align',
  'link',
  'code',
  'strike',
  'blockquote',
  'code-block',
  'formula',
  'image',
];

export type RichTextEditorElementProps = {
  name: string;
  value?: string;
  disabled?: boolean;
  onBlur?: OnBlurCallback;
  onChange?: OnChangeCallback;
  limit?: number;
  variant?: 'default' | 'publicEvent';
  rawTextLimit?: number;
};

export type RichTextEditorProps = Omit<$RichTextEditorProps, 'onBlur' | 'onChange' | 'value'> &
  RichTextEditorElementProps;

const RTEValueLengthMessage = ({ value, rawTextLimit }: { value?: string; rawTextLimit: number }) => {
  let body;
  const CharacterCountTooltip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Tooltip arrow={false} title="Character count includes hidden formatting characters.">
      {children}
    </Tooltip>
  );

  if ((value?.length || 0) > rawTextLimit) {
    body = (
      <WarningText>
        <WarningOutlined />{' '}
        <CharacterCountTooltip>
          <span>Text is too long</span>
        </CharacterCountTooltip>
      </WarningText>
    );
  } else {
    const remainingChars = rawTextLimit - (value?.length || 0);
    body =
      remainingChars <= 100 ? (
        <CharacterCountTooltip>
          {remainingChars} characters remaining <InfoCircleOutlined style={{ paddingLeft: '3px' }} />
        </CharacterCountTooltip>
      ) : (
        ''
      );
  }

  return <div> {body} </div>;
};

function getToolbarId() {
  return `rte-toolbar${++toolbarCnt}`;
}
const ReactQuill = typeof window === 'object' ? require('react-quill') : () => false;
const RichTextEditor = React.memo(function RichTexEditor({
  name,
  value,
  disabled = false,
  onBlur,
  onChange,
  limit,
  variant = 'default',
  rawTextLimit,
  ...props
}: RichTextEditorProps) {
  const wrapperRef = useRef<any>();
  const quillRef = useRef<any>();
  const toolbarId = useRef<string>(getToolbarId());
  const getEditor = () => {
    const quill = quillRef.current.getEditor();
    const unprivilegedEditor = quillRef.current.makeUnprivilegedEditor(quill);
    return unprivilegedEditor;
  };

  const getEditorHTML = () => {
    const unprivilegedEditor = getEditor();
    const html = unprivilegedEditor.getHTML();
    return html;
  };

  useEffect(() => {
    const pickerLabel = wrapperRef.current.querySelector(`.${TOOLBAR_CLASS_NAME} .ql-picker-label`);
    pickerLabel && pickerLabel.setAttribute('aria-label', 'choose text size');
    const pickerOptions = wrapperRef.current.querySelectorAll(`.${TOOLBAR_CLASS_NAME} .ql-picker-item`);
    pickerOptions.forEach((option: any) => {
      option.setAttribute('aria-label', option.getAttribute('data-label'));
    });
  }, []);

  const handleChange =
    onChange &&
    ((value: string, delta: Quill.Delta, source: Quill.Sources, editor: any) => {
      if (limit) {
        const words = editor.getText().split(' ');
        if (words.length > limit) {
          value = words.splice(0, limit).join(' ');
        }
      }

      if (!isNil(delta) && source === 'user') {
        onChange({ name, value });
      }
    });

  const handleKeyDown = (e: any) => {
    if (limit) {
      const editor = getEditor();
      const value = editor
        .getText()
        .replace(/\r?\n|\r/g, ' ')
        .split(' ')
        .filter(Boolean);
      if (e.key !== 'Backspace' && value.length > limit) {
        e.preventDefault();
      }
    }
  };

  const handleBlur =
    onBlur &&
    ((previousRange: Quill.RangeStatic, source: Quill.Sources) => {
      if (source === 'user') {
        if (previousRange.index === 0 && previousRange.length === 0) {
          onBlur({ name, value: '' }); //It is empty content for sure.
          return;
        }
        onBlur({ name, value: getEditorHTML() });
      }
    });

  return (
    <>
      <RTEContainer ref={wrapperRef} data-rte-disabled={disabled}>
        <CustomToolbar id={toolbarId.current} variant={variant} />
        <ReactQuill
          readOnly={disabled}
          ref={quillRef}
          modules={modules(toolbarId.current)}
          formats={formats}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          value={value || ''}
          {...props}
        />
        {rawTextLimit && <RTEValueLengthMessage value={value} rawTextLimit={rawTextLimit} />}
      </RTEContainer>
    </>
  );
});

export default RichTextEditor;
